import React, {
  memo, useCallback, useEffect, useState,
} from 'react';
import {
  Button, Col, Divider, Row, Space, Typography,
} from 'antd';
import { Content } from 'antd/es/layout/layout';

import GET_UNDISPATCHED_SCAN_IDS from '@graphql/TH_getAllUndispatchedScanIds';
import GET_DISPATCHED_SCAN_IDS from '@graphql/TH_getAllDispatchedScanIds';
import DISPATCH_SCAN_ID from '@graphql/TH_dispatchScanId';
import UNDISPATCH_SCAN_ID from '@graphql/TH_undispatchScanId';

import useList from '@hooks/useList';
import useCreateUpdateRecord from '@hooks/useCreateUpdateRecord';

import { CardStyled } from '@components/Styles/CardStyles';
import { TableSmall, TableSubSmall } from '@components/Styles/TableStyles';
import { parseAndToFixed } from '@utils/helpers/helpers';

function OutboundJobDispatch({
  currentTab,
  onSetModalActions,
  record,
}) {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowKeysDispatched, setSelectedRowKeysDispatched] = useState([]);
  const [formDataUndispatch, setFormDataUndispatch] = useState(null);
  const [formDataDispatch, setFormDataDispatch] = useState(null);

  const { loading: listUndispatchLoading, data: undispatchScanIdList, refetch: refetchUndispatch } = useList({
    query: GET_UNDISPATCHED_SCAN_IDS,
    variables: {
      CustomerPartyFk: record?.Customer_Party_Fk,
      WarehouseOutId: record?.Id,
    },
  });

  const { loading: listDispatchLoading, data: dispatchScanIdList, refetch: refetchDispatch } = useList({
    query: GET_DISPATCHED_SCAN_IDS,
    variables: {
      CustomerPartyFk: record?.Customer_Party_Fk,
      WarehouseOutId: record?.Id,
    },
  });

  const { loading: loadingPick } = useCreateUpdateRecord({
    formData: formDataDispatch,
    mutation: DISPATCH_SCAN_ID,
    callback: () => handleSuccess(),
  });

  const { loading: loadingUnpick } = useCreateUpdateRecord({
    formData: formDataUndispatch,
    mutation: UNDISPATCH_SCAN_ID,
    callback: () => handleSuccess(),
  });

  const handleSuccess = useCallback(() => {
    refetchUndispatch();
    refetchDispatch();
  }, [record?.Id]);

  useEffect(() => {
    if (currentTab === 'dispatch') {
      const isDisabledAllButton = selectedRowKeysDispatched.length === 0 && selectedRowKeys.length === 0;
      onSetModalActions([
        <div key={1}>&nbsp;</div>,
        <Space key={2}>
          <Button type="default" onClick={handleDispatch} disabled={loadingPick || selectedRowKeysDispatched.length > 0 || isDisabledAllButton} loading={loadingPick}>
            Dispatch
          </Button>
          <Button type="default" onClick={handleUndispatch} disabled={loadingUnpick || selectedRowKeys.length > 0 || isDisabledAllButton} loading={loadingUnpick}>
            Undispatch
          </Button>
        </Space>,
      ]);
    }
  }, [currentTab, selectedRowKeys, selectedRowKeysDispatched, loadingPick, loadingUnpick]);

  useEffect(() => {
    if (currentTab === 'dispatch') {
      refetchUndispatch();
      refetchDispatch();
    }
  }, [currentTab]);

  const rowSelectionDispatched = {
    onChange: (selectedRowKeys) => {
      setSelectedRowKeys([]);
      setSelectedRowKeysDispatched(selectedRowKeys);
    },
    getCheckboxProps: ({ AttachedPOLineItemToScanId }) => ({
      disabled: AttachedPOLineItemToScanId && AttachedPOLineItemToScanId.length <= 0,
    }),
  };

  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedRowKeysDispatched([]);
      setSelectedRowKeys(selectedRowKeys);
    },
    getCheckboxProps: ({ AttachedPOLineItemToScanId }) => ({
      disabled: AttachedPOLineItemToScanId && AttachedPOLineItemToScanId.length <= 0,
    }),
  };

  const handleDispatch = useCallback(() => {
    setFormDataDispatch({
      ScanId_POPartLocation_Fks: selectedRowKeys,
      WarehouseOut_Fk: record?.Id,
    });
  }, [selectedRowKeys, record?.Id]);

  const handleUndispatch = useCallback(() => {
    setFormDataUndispatch({
      ScanId_POPartLocation_Fks: selectedRowKeysDispatched,
      WarehouseOut_Fk: record?.Id,
    });
  }, [selectedRowKeysDispatched, record?.Id]);

  const scanIdColumns = [
    {
      title: 'Scan ID',
      dataIndex: 'ScanId',
      key: 'ScanId',
    },
    {
      title: 'Total Quantity',
      dataIndex: 'TotalQuantity',
      key: 'TotalQuantity',
    },
    {
      title: 'Warehouse',
      dataIndex: 'Warehouse',
      key: 'Warehouse',
      render: (text) => (
        <span style={{ userSelect: 'text' }}>
          {text?.Code && `[${text?.Code}] ${text?.Name}`}
        </span>
      ),
    },
    {
      title: 'Storage Location',
      dataIndex: 'StorageLocation',
      key: 'StorageLocation',
      render: (text) => (
        <span style={{ userSelect: 'text' }}>
          {text?.Code}
        </span>
      ),
    },
    {
      title: 'Dimensions (INCH) L x W x H',
      dataIndex: 'DimensionsIn',
      key: 'DimensionsIn',
      render: (_, record) => (
        <span style={{ userSelect: 'text' }}>
          {`${parseAndToFixed(record?.LengthInch) || 0} x ${parseAndToFixed(record?.WidthInch) || 0} x ${parseAndToFixed(record?.HeightInch) || 0}`}
        </span>
      ),
    },
    {
      title: 'Dimensions (CM) L x W x H',
      dataIndex: 'DimensionsCm',
      key: 'DimensionsCm',
      render: (_, record) => (
        <span style={{ userSelect: 'text' }}>
          {`${parseAndToFixed(record?.LengthCm) || 0} x ${parseAndToFixed(record?.WidthCm) || 0} x ${parseAndToFixed(record?.HeightCm) || 0}`}
        </span>
      ),
    },
    {
      title: 'Volume (CBM)',
      dataIndex: 'VolumeCbm',
      key: 'VolumeCbm',
      render: (text) => (
        <span style={{ userSelect: 'text' }}>
          {`${parseAndToFixed(text, 3) || ''}`}
        </span>
      ),
    },
    {
      title: 'Volume (CFT)',
      dataIndex: 'VolumeCft',
      key: 'VolumeCft',
      render: (text) => (
        <span style={{ userSelect: 'text' }}>
          {`${parseAndToFixed(text, 3) || ''}`}
        </span>
      ),
    },
    {
      title: 'Weight (KG)',
      dataIndex: 'WeightKg',
      key: 'WeightKg',
      render: (text) => (
        <span style={{ userSelect: 'text' }}>
          {`${parseAndToFixed(text) || ''}`}
        </span>
      ),
    },
    {
      title: 'Weight (LBS)',
      dataIndex: 'WeightLbs',
      key: 'WeightLbs',
      render: (text) => (
        <span style={{ userSelect: 'text' }}>
          {`${parseAndToFixed(text) || ''}`}
        </span>
      ),
    },
  ];

  const undispatchedScanIdColumns = [...scanIdColumns];

  undispatchedScanIdColumns.splice(1, 0, {
    title: 'Label Series',
    dataIndex: 'LabelSeries',
    key: 'LabelSeries',
    render: (text) => (
      <span style={{ userSelect: 'text' }}>
        {`${text || ''} of ${undispatchScanIdList.length}`}
      </span>
    ),
  });

  const dispatchedScanIdColumns = [...scanIdColumns];

  dispatchedScanIdColumns.splice(1, 0, {
    title: 'Label Series',
    dataIndex: 'LabelSeries',
    key: 'LabelSeries',
    render: (text) => (
      <span style={{ userSelect: 'text' }}>
        {`${text || ''} of ${dispatchScanIdList.length}`}
      </span>
    ),
  });

  const lineItemColumns = [
    {
      title: 'Line Item No.',
      dataIndex: 'LineNumber',
      key: 'LineNumber',
    },
    {
      title: 'Part No.',
      dataIndex: 'Part_Number',
      key: 'Part_Number',
    },
    {
      title: 'Part Description.',
      dataIndex: 'Part_Description',
      key: 'Part_Description',
    },
    {
      title: 'Assigned Quantity',
      dataIndex: 'AssignedQuantity',
      key: 'AssignedQuantity',
    },
  ];

  const expandedRowRender = ({ ScanId, AttachedPOLineItemToScanId }) => (
    <>
      {AttachedPOLineItemToScanId.length > 0 ? (
        <div style={{ padding: '18px 12px', backgroundColor: '#eee' }}>
          <TableSubSmall
            size="small"
            dataSource={AttachedPOLineItemToScanId}
            columns={lineItemColumns}
            pagination={false}
            rowKey={(record) => `${ScanId}-${record?.Id}`}
            scroll={{
              x: true,
            }}
          />
        </div>
      ) : (<div>No Items</div>)}
    </>
  );

  return (
    <Content style={{ padding: 16 }}>
      <Row gutter={16}>
        <Col xs={24}>
          <CardStyled>
            <Typography.Title level={5}>Undispatched Scan IDs</Typography.Title>
            <TableSmall
              style={{ marginTop: 24 }}
              size="small"
              dataSource={undispatchScanIdList}
              loading={listUndispatchLoading}
              columns={undispatchedScanIdColumns}
              pagination={false}
              scroll={{
                x: true,
              }}
              rowKey="Id"
              rowSelection={{
                type: 'checkbox',
                selectedRowKeys,
                ...rowSelection,
              }}
              expandable={{
                rowExpandable: ({ AttachedPOLineItemToScanId }) => AttachedPOLineItemToScanId && AttachedPOLineItemToScanId.length > 0,
                defaultExpandAllRows: true,
                expandedRowRender,
              }}
            />
          </CardStyled>
          <Divider />
          <CardStyled>
            <Typography.Title level={5}>Dispatched Scan IDs</Typography.Title>
            <TableSmall
              style={{ marginTop: 24 }}
              size="small"
              dataSource={dispatchScanIdList}
              loading={listDispatchLoading}
              columns={dispatchedScanIdColumns}
              pagination={false}
              scroll={{
                x: true,
              }}
              rowKey="Id"
              rowSelection={{
                type: 'checkbox',
                selectedRowKeys: selectedRowKeysDispatched,
                ...rowSelectionDispatched,
              }}
              expandable={{
                rowExpandable: ({ AttachedPOLineItemToScanId }) => AttachedPOLineItemToScanId && AttachedPOLineItemToScanId.length > 0,
                defaultExpandAllRows: true,
                expandedRowRender,
              }}
            />
          </CardStyled>
        </Col>
      </Row>
    </Content>
  );
}

export default memo(OutboundJobDispatch);
